/**
 * Governance del diario di salute lato atleta, mostrata nel profilo.
 *
 * Dà all'atleta il pieno controllo dei propri dati sanitari: consultarli,
 * disattivare il tracciamento (i dati restano) o cancellarli del tutto.
 * Resta visibile anche prima dell'attivazione, così l'atleta può configurarlo.
 * Spec: docs/wellness/00-analisi-e-piano.md
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { fetchAPI } from "../../lib/utils";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { HeartPulse, ExternalLink, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";

export function WellnessGovernanceCard() {
	const navigate = useNavigate();
	const { language, t } = useLanguage();
	const { checkAuth } = useAuth();
	const [loading, setLoading] = useState(true);
	const [config, setConfig] = useState(null);
	const [catalog, setCatalog] = useState([]);
	const [selectedFields, setSelectedFields] = useState([]);
	const [enabled, setEnabled] = useState(false);
	const [working, setWorking] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [data, catalogData] = await Promise.all([
				fetchAPI("/wellness/me/config").catch(() => null),
				fetchAPI(`/wellness/catalog?language=${language}`).catch(() => null),
			]);
			setConfig(data);
			setEnabled(!!data?.enabled);
			setCatalog(catalogData?.fields || []);
			setSelectedFields(
				data?.fields?.length ? data.fields : catalogData?.default_fields || [],
			);
		} finally {
			setLoading(false);
		}
	}, [language]);

	useEffect(() => {
		load();
	}, [load]);

	const handleSaveConfig = async () => {
		if (enabled && selectedFields.length === 0) {
			toast.error(t("wellnessSelectAtLeastOne"));
			return;
		}
		if (
			config?.enabled &&
			!enabled &&
			!window.confirm(t("wellnessDisableConfirm"))
		) {
			setEnabled(true);
			return;
		}
		setWorking(true);
		try {
			const updated =
				config?.enabled && !enabled
					? await fetchAPI("/wellness/me/disable", { method: "POST" })
					: await fetchAPI("/wellness/me/config", {
							method: "PUT",
							body: JSON.stringify({
								enabled,
								fields: selectedFields,
								reminder_enabled: config?.reminder_enabled !== false,
							}),
						});
			setConfig(updated);
			await checkAuth();
			window.dispatchEvent(new Event("wellness-updated"));
			if (enabled && updated?.consent_required) {
				navigate("/wellness");
			} else {
				toast.success(
					enabled
						? t("wellnessConfigurationSaved")
						: t("wellnessTrackingDisabled"),
				);
			}
		} catch {
			toast.error(enabled ? t("wellnessSaveError") : t("wellnessError"));
		} finally {
			setWorking(false);
		}
	};

	const toggleField = (key) => {
		setSelectedFields((previous) =>
			previous.includes(key)
				? previous.filter((field) => field !== key)
				: [...previous, key],
		);
	};

	const renderFieldGroup = (title, fields) => (
		<section className="space-y-2">
			<p className="label-tactical">{title}</p>
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
				{fields.map((field) => {
					const selected = selectedFields.includes(field.key);
					return (
						<button
							key={field.key}
							type="button"
							aria-pressed={selected}
							disabled={working}
							onClick={() => toggleField(field.key)}
							className={`text-left rounded-sm border px-3 py-2 min-h-11 transition-colors ${
								selected
									? "border-volt/60 bg-volt/10"
									: "border-border bg-secondary hover:border-volt/40"
							}`}
						>
							<span className="flex items-center justify-between gap-2 text-sm font-medium">
								{field.label}
								<span
									aria-hidden="true"
									className={`w-4 h-4 rounded-sm border flex items-center justify-center text-[10px] ${
										selected
											? "bg-volt border-volt text-white"
											: "border-border"
									}`}
								>
									{selected ? "✓" : ""}
								</span>
							</span>
							{field.unit && (
								<span className="text-[10px] text-muted-foreground">
									{field.unit}
								</span>
							)}
						</button>
					);
				})}
			</div>
		</section>
	);

	const handleDelete = async () => {
		if (!window.confirm(t("wellnessDeleteAllConfirm"))) {
			return;
		}
		setWorking(true);
		try {
			await fetchAPI("/wellness/me/entries", { method: "DELETE" });
			await load();
			await checkAuth();
			toast.success(t("wellnessDataDeleted"));
		} catch {
			toast.error(t("wellnessError"));
		} finally {
			setWorking(false);
		}
	};

	if (loading) return null;

	return (
		<Card className="bg-card border-border rounded-sm">
			<CardContent className="p-4 space-y-3">
				<div className="flex items-center gap-2">
					<HeartPulse className="w-5 h-5 text-volt" />
					<div className="min-w-0">
						<p className="font-heading text-sm">{t("wellnessDiaryTitle")}</p>
						<p className="text-xs text-muted-foreground">
							{t("wellnessDiarySubtitle")}
						</p>
					</div>
				</div>

				<div className="flex flex-wrap items-center gap-2 text-xs">
					{!enabled ? (
						<Badge
							variant="outline"
							className="rounded-sm text-muted-foreground border-border"
						>
							{t("wellnessInactive")}
						</Badge>
					) : config?.consent_required !== false ? (
						<Badge
							variant="outline"
							className="rounded-sm text-amber-400 border-amber-500/40"
						>
							{t("wellnessConsentToAccept")}
						</Badge>
					) : (
						<Badge className="rounded-sm bg-green-500/20 text-green-400 border-0">
							{t("wellnessActive")}
						</Badge>
					)}
				</div>

				<div className="flex items-center justify-between gap-3 p-3 rounded-sm bg-secondary border border-border">
					<div className="min-w-0">
						<p className="text-sm font-medium">{t("wellnessEnabled")}</p>
						<p className="text-xs text-muted-foreground">
							{t("wellnessEnabledDescription")}
						</p>
					</div>
					<Switch
						checked={enabled}
						disabled={working}
						onCheckedChange={setEnabled}
						aria-label={t("wellnessEnabled")}
					/>
				</div>

				<p className="text-xs text-muted-foreground">
					{t("wellnessPrivacyDescription")}
				</p>

				{renderFieldGroup(
					t("wellnessSubjectiveParameters"),
					catalog.filter((field) => field.category === "subjective"),
				)}
				{renderFieldGroup(
					t("wellnessObjectiveParameters"),
					catalog.filter((field) => field.category === "objective"),
				)}

				{enabled && selectedFields.length === 0 && (
					<p className="text-xs text-amber-400">
						{t("wellnessSelectAtLeastOne")}
					</p>
				)}

				<Button
					className="w-full rounded-sm bg-volt hover:bg-volt/90"
					disabled={working || (enabled && selectedFields.length === 0)}
					onClick={handleSaveConfig}
				>
					{working && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
					{t("wellnessSaveConfiguration")}
				</Button>

				{enabled && (
					<div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
						<Button
							variant="outline"
							className="rounded-sm border-dashed border-volt/40 text-volt hover:border-volt/70"
							onClick={() => navigate("/wellness")}
						>
							<ExternalLink className="w-3.5 h-3.5 mr-1" />
							{t("wellnessOpenDiary")}
						</Button>
						<Button
							variant="outline"
							className="rounded-sm text-destructive hover:text-destructive"
							disabled={working}
							onClick={handleDelete}
						>
							<Trash2 className="w-3.5 h-3.5 mr-1" />
							{t("wellnessDeleteData")}
						</Button>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
